import { styles, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as style from './Aura.module.css';

const SENSITIVITY = 1.2;

const Waveform = ({ active = false, conversation }) => {
  const [audioLevel, setAudioLevel] = useState(0);
  const [inputAudioLevel, setInputAudioLevel] = useState(0);
  const animationFrameRef = useRef(null);
  const isActiveRef = useRef(false);

  const analyzeAudio = useCallback(() => {
    if (!isActiveRef.current) return;

    const outputDataArray = conversation.getOutputByteFrequencyData();
    const inputDataArray = conversation.getInputByteFrequencyData();

    let outputLevel = 0;
    let inputLevel = 0;

    if (outputDataArray && outputDataArray.length > 0) {
      const sum = outputDataArray.reduce((acc, value) => acc + value, 0);
      const average = sum / outputDataArray.length;
      outputLevel = Math.min((average / 128) * SENSITIVITY, 1);
    }

    if (inputDataArray && inputDataArray.length > 0) {
      const sum = inputDataArray.reduce((acc, value) => acc + value, 0);
      const average = sum / inputDataArray.length;
      inputLevel = Math.min((average / 128) * SENSITIVITY, 1);
    }

    setAudioLevel(outputLevel);
    setInputAudioLevel(inputLevel);

    animationFrameRef.current = requestAnimationFrame(analyzeAudio);
  }, [conversation]);

  const stopAudioAnalysis = useCallback(() => {
    isActiveRef.current = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setAudioLevel(0);
    setInputAudioLevel(0);
  }, []);

  const startAudioAnalysis = useCallback(() => {
    isActiveRef.current = true;
    analyzeAudio();
  }, [analyzeAudio]);

  useEffect(() => {
    if (active) startAudioAnalysis();
    else stopAudioAnalysis();

    return () => {
      stopAudioAnalysis();
    };
  }, [active, startAudioAnalysis, stopAudioAnalysis]);

  const isUserSpeaking = inputAudioLevel > audioLevel && inputAudioLevel > 0.1;
  const currentAudioLevel = isUserSpeaking ? inputAudioLevel : audioLevel;

  const getAuraScale = () => {
    if (!active || currentAudioLevel === 0) return 1;
    return 1 + currentAudioLevel * 0.5;
  };

  return (
    <View
      className={styles(style.waveform, active && style.active)}
      style={{
        '--aura-scale': getAuraScale(),
        '--aura-opacity': !active ? 0 : Math.max(0.33, currentAudioLevel * 0.66),
      }}
    >
      <View className={style.aura} />
      <View className={style.pulse} />
      <View className={style.glow} />
      <View className={style.content} />
    </View>
  );
};

Waveform.displayName = 'Mirai:Core:Chat.Aura.Waveform';

Waveform.propTypes = {
  active: PropTypes.bool,
  conversation: PropTypes.shape({
    getOutputByteFrequencyData: PropTypes.func,
    getInputByteFrequencyData: PropTypes.func,
  }),
};

export { Waveform };
